import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../../lib/api";
import { EventCard, VenueCard, ArtistCard, cardGrid } from "../Cards";

const PLACEHOLDER = "https://images.pexels.com/photos/3122799/pexels-photo-3122799.jpeg";
const px = (v) => (v || v === 0) ? `${v}px` : undefined;

export function outerStyle(s = {}) {
  return {
    backgroundColor: s.bg || undefined,
    paddingTop: px(s.paddingY), paddingBottom: px(s.paddingY),
    paddingLeft: px(s.paddingX), paddingRight: px(s.paddingX),
    marginTop: px(s.marginTop), marginBottom: px(s.marginBottom),
  };
}
const container = (s = {}) => ({ maxWidth: s.maxWidth ? px(s.maxWidth) : "100%", marginLeft: "auto", marginRight: "auto", width: "100%" });
const textStyle = (s = {}) => ({ color: s.color || undefined, textAlign: s.align || undefined, fontSize: px(s.fontSize), fontWeight: s.fontWeight || undefined, lineHeight: s.lineHeight || undefined });

function deviceFor(w) { return w < 640 ? "mobile" : w < 1024 ? "tablet" : "desktop"; }
export function useDevice() {
  const [d, setD] = useState(() => deviceFor(typeof window !== "undefined" ? window.innerWidth : 1280));
  useEffect(() => {
    const onR = () => setD(deviceFor(window.innerWidth));
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  return d;
}
function colsFor(columns, device) {
  const c = Number(columns) || 3;
  if (device === "mobile") return 1;
  if (device === "tablet") return Math.min(2, c);
  return c;
}
function embedUrl(url) {
  if (!url) return "";
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

function SmartLink({ href, children, className, style }) {
  if (!href) return <span className={className} style={style}>{children}</span>;
  const ext = /^(https?:|mailto:|tel:|#)/.test(href);
  if (ext) return <a href={href} className={className} style={style} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">{children}</a>;
  return <Link to={href} className={className} style={style}>{children}</Link>;
}

function DirectoryBlock({ content, s }) {
  const [items, setItems] = useState([]);
  const source = content.source || "events";
  const cardStyle = content.cardStyle || "default";
  useEffect(() => {
    api.get(`/${source}`).then((r) => setItems((r.data || []).slice(0, Number(content.count) || 3))).catch(() => setItems([]));
  }, [source, content.count]);
  const Card = source === "venues" ? VenueCard : source === "artists" ? ArtistCard : EventCard;
  const propKey = source === "venues" ? "v" : source === "artists" ? "a" : "e";
  return (
    <section style={outerStyle(s)}>
      <div style={container(s)}>
        {content.heading && <h2 className="font-display font-black text-3xl mb-8" style={{ color: s.color || "#fff", textAlign: s.align }}>{content.heading}</h2>}
        <div className={`grid gap-5 ${cardGrid(cardStyle)}`} data-testid="directory-cards">
          {items.map((it) => <Card key={it.id} {...{ [propKey]: it }} style={cardStyle} />)}
        </div>
        {items.length === 0 && <p className="text-zinc-600 text-sm">No {source} to show yet.</p>}
      </div>
    </section>
  );
}

export function BlockContent({ block, device = "desktop" }) {
  const s = block.style || {};
  const c = block.content || {};
  switch (block.type) {
    case "hero": {
      const align = c.align || "center";
      const justify = align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start";
      return (
        <section style={{ position: "relative", display: "flex", alignItems: "center", minHeight: px(c.minHeight) || "520px", ...outerStyle(s) }}>
          {c.image && <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${fileUrl(c.image)})`, backgroundSize: "cover", backgroundPosition: "center" }} />}
          <div style={{ position: "absolute", inset: 0, backgroundColor: `rgba(0,0,0,${(c.overlay ?? 50) / 100})` }} />
          <div style={{ position: "relative", width: "100%", ...container(s), padding: `48px ${px(s.paddingX) || "24px"}`, textAlign: align }}>
            {c.eyebrow && <p className="mb-3" style={{ color: "var(--primary)", letterSpacing: "0.18em", textTransform: "uppercase", fontSize: "13px", fontWeight: 700 }}>{c.eyebrow}</p>}
            {c.heading && <h1 className="font-display" style={{ color: s.color || "#fff", fontSize: "clamp(2.4rem,5vw,4.2rem)", fontWeight: 900, lineHeight: 1.05 }}>{c.heading}</h1>}
            {c.subheading && <p style={{ color: "rgba(255,255,255,0.82)", fontSize: "clamp(1rem,2vw,1.25rem)", marginTop: "16px", maxWidth: "640px", marginLeft: align === "center" ? "auto" : 0, marginRight: align === "center" ? "auto" : 0 }}>{c.subheading}</p>}
            {(c.primaryLabel || c.secondaryLabel) && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginTop: "32px", justifyContent: justify }}>
                {c.primaryLabel && <SmartLink href={c.primaryHref} className="font-bold transition-transform hover:scale-105" style={{ display: "inline-block", backgroundColor: "var(--primary)", color: "#fff", borderRadius: "9999px", padding: "14px 32px" }}>{c.primaryLabel}</SmartLink>}
                {c.secondaryLabel && <SmartLink href={c.secondaryHref} className="font-bold border border-white/30 text-white transition-colors hover:bg-white/10" style={{ display: "inline-block", borderRadius: "9999px", padding: "14px 32px" }}>{c.secondaryLabel}</SmartLink>}
              </div>
            )}
          </div>
        </section>
      );
    }
    case "heading": {
      const Tag = c.level || "h2";
      return <section style={outerStyle(s)}><div style={container(s)}><Tag className="font-display" style={textStyle(s)}>{c.text}</Tag></div></section>;
    }
    case "text":
      return <section style={outerStyle(s)}><div style={container(s)}><p style={{ ...textStyle(s), whiteSpace: "pre-wrap" }}>{c.text}</p></div></section>;
    case "image": {
      const img = <img src={fileUrl(c.src) || PLACEHOLDER} alt={c.alt || ""} style={{ borderRadius: px(s.radius), maxWidth: "100%", display: "inline-block" }} />;
      return <section style={outerStyle(s)}><div style={{ ...container(s), textAlign: s.align || "center" }}>{c.href ? <SmartLink href={c.href}>{img}</SmartLink> : img}</div></section>;
    }
    case "button": {
      const justify = (s.align || "center");
      return <section style={outerStyle(s)}><div style={{ ...container(s), textAlign: justify }}>
        <SmartLink href={c.href} className="font-bold transition-transform hover:scale-105" style={{ display: "inline-block", backgroundColor: s.bg || "var(--primary)", color: s.color || "#fff", borderRadius: s.radius != null ? px(s.radius) : "9999px", padding: "14px 32px", fontWeight: s.fontWeight || 700, fontSize: px(s.fontSize) }}>{c.label}</SmartLink>
      </div></section>;
    }
    case "cta":
      return <section style={outerStyle(s)}><div style={{ ...container(s), textAlign: s.align || "center" }}>
        {c.heading && <h2 className="font-display font-black" style={{ color: s.color || "#fff", fontSize: "clamp(1.8rem,4vw,2.8rem)" }}>{c.heading}</h2>}
        {c.text && <p style={{ color: "rgba(255,255,255,0.7)", maxWidth: "620px", margin: "12px auto 0" }}>{c.text}</p>}
        {c.buttonLabel && <div style={{ marginTop: "24px" }}><SmartLink href={c.buttonHref} className="font-bold transition-transform hover:scale-105" style={{ display: "inline-block", backgroundColor: "var(--primary)", color: "#fff", borderRadius: "9999px", padding: "14px 32px" }}>{c.buttonLabel}</SmartLink></div>}
      </div></section>;
    case "cards": {
      const cols = colsFor(c.columns, device);
      return <section style={outerStyle(s)}><div style={container(s)}>
        {c.heading && <h2 className="font-display font-black text-3xl mb-8" style={{ color: s.color || "#fff", textAlign: s.align }}>{c.heading}</h2>}
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap: "20px" }}>
          {(c.items || []).map((it, i) => {
            const inner = (<>
              {it.image && <div className="aspect-[4/3] overflow-hidden rounded-xl mb-4"><img src={fileUrl(it.image)} alt={it.title || ""} className="w-full h-full object-cover" /></div>}
              {it.title && <p className="font-display font-bold text-lg" style={{ color: s.color || "#fff" }}>{it.title}</p>}
              {it.text && <p className="text-sm text-zinc-400 mt-1">{it.text}</p>}
            </>);
            return it.href
              ? <SmartLink key={i} href={it.href} className="block bg-[#111] border border-white/10 rounded-2xl p-5 hover:border-white/25 transition-colors">{inner}</SmartLink>
              : <div key={i} className="bg-[#111] border border-white/10 rounded-2xl p-5">{inner}</div>;
          })}
        </div>
      </div></section>;
    }
    case "gallery": {
      const cols = colsFor(c.columns, device);
      return <section style={outerStyle(s)}><div style={container(s)}>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap: "14px" }}>
          {(c.images || []).map((im, i) => (
            <div key={i} className="overflow-hidden" style={{ borderRadius: px(s.radius) || "14px" }}>
              <img src={fileUrl(im.src) || PLACEHOLDER} alt={im.alt || ""} className="w-full h-full object-cover aspect-square" />
            </div>
          ))}
        </div>
      </div></section>;
    }
    case "directory":
      return <DirectoryBlock content={c} s={s} />;
    case "video": {
      const src = embedUrl(c.url);
      return <section style={outerStyle(s)}><div style={container(s)}>
        {src ? <div style={{ position: "relative", paddingTop: "56.25%", borderRadius: px(s.radius) || "14px", overflow: "hidden" }}>
          <iframe src={src} title="video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }} />
        </div> : <div className="text-zinc-600 text-sm text-center py-10 border border-dashed border-white/15 rounded-xl">Add a YouTube or Vimeo URL in the Content tab</div>}
      </div></section>;
    }
    case "divider":
      return <section style={outerStyle(s)}><div style={container(s)}><hr style={{ border: 0, borderTop: `1px solid ${s.borderColor || "#27272a"}` }} /></div></section>;
    case "spacer":
      return <div style={{ height: px(c.height) || "48px" }} />;
    default:
      return null;
  }
}

export function PageBlocks({ blocks, device }) {
  return <>{(blocks || []).map((b) => <BlockContent key={b.id} block={b} device={device} />)}</>;
}
